import React, { memo, useEffect, useMemo } from 'react';
import { Form, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import flowRight from 'lodash-es/flowRight';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import { lookupsJson, leaseLoanStates, getValue } from '@ourbranch/lookups';

import { appConfig, brandName } from 'brand-config';
import {
  getTrailerComprehensiveDeductibleOptions,
  getTrailerDeductibleOptions
} from 'common/helpers/trailer-deductible-helpers';
import Field from 'core/components/form/form.v2';
import useOptions from 'core/hooks/use-options';
import { tooltipHoverTexts } from 'core/helpers/constants';
import withDatePicker from 'core/components/with-date-picker';
import { LabelTooltip } from 'core/components/label-tooltip';
import { Label } from 'core/components/label';
import RemoveButton from 'core/components/remove-button';
import CollapsibleCard from 'core/components/collapsible-card';
import styles from './trailer.styles';

// Resolve the type's display name brand-aware: CFM's R/U codes live in trailerTypeByCarrier, not the
// base A2..G2 list (#35836).
const getTrailerHeader = (item, state) =>
  `${item.year || ''} ${getValue('trailerType', item.type, state, undefined, brandName) || 'New Trailer'}`;

const Trailer = ({
  fieldName,
  classes,
  item = {},
  index,
  removeFromList,
  onRemove,
  disabled,
  offer,
  policy,
  openSignalOnError = 0
}) => {
  const { isBixOfferOrState, state: offerState } = offer;
  const { isBix: policyIsBix, state: policyState } = policy;
  const isBix = policyIsBix || isBixOfferOrState;
  const state = policyState || offerState || item?.garageLocation?.state || null;
  const { deductibleCollision, deductibleComprehensive } = item;

  const { setTouched, errors, setFieldValue } = useFormikContext();

  // Brand-aware type list: CFM offers only Recreational/Utility (ClarionDoor's closed R|U set —
  // any other code zeroes the whole policy's rate, #34360); other brands keep the base list. #35836
  const trailerTypeOptions = useOptions('trailerType', state, item?.type);
  // CFM labels the trailer value "Stated Amount" and doesn't collect contents (#35838).
  const statedAmountLabel = appConfig?.featureFlags?.trailerStatedAmountLabel ?? false;
  const hideContents = appConfig?.featureFlags?.hideTrailerContents ?? false;

  // Trailer deductibles come from the carrier's trailer-specific list where one is filed (CFM tops
  // out at 1000 while the vehicle lists run to 2500), falling back to the vehicle list otherwise.
  // See getTrailerDeductibleOptions for why exceeding the cap is worse than it looks. #34400
  const deductibleCollisionOptions = useMemo(
    () => getTrailerDeductibleOptions('deductibleCollision', state, brandName).filter(({ id }) => !id.endsWith('LC')),
    [state]
  );

  // A trailer has no separate glass coverage, so comprehensive is presented as a plain deductible.
  // getTrailerComprehensiveDeductibleOptions collapses glass variants to their base amount, which
  // keeps glass-only states (KY, MA) selectable instead of empty. #35294
  const deductibleComprehensiveOptions = useMemo(
    () => getTrailerComprehensiveDeductibleOptions(state, brandName),
    [state]
  );

  const leaseLoanAvailable = useMemo(() => {
    // lease loan in all BIX and all GSNIC that isn't our original five
    return (
      deductibleCollision !== 'NONE' &&
      deductibleComprehensive !== 'NONE' &&
      (isBix ? leaseLoanStates[state]?.isBix : leaseLoanStates[state]?.isNotBix)
    );
  }, [deductibleCollision, deductibleComprehensive, isBix, state]);

  useEffect(() => {
    if (Object.keys(errors).length) {
      setTouched(errors);
    }
  }, [errors, setTouched]);

  useEffect(() => {
    if (!leaseLoanAvailable) {
      setFieldValue(`${fieldName}.coverageLoan`, false);
    }
  }, [leaseLoanAvailable, fieldName, setFieldValue]);

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <Label type="infoSubtitle">{getTrailerHeader(item, state)}</Label>
        </div>
      }
      contentRight={<RemoveButton disabled={disabled} mode="big" onClick={() => onRemove(index, removeFromList)} />}
      initialOpen={item.initialOpen}
      // Open this card when the validation-errors modal navigates to a trailer error (e.g. empty
      // VIN). openSignal is open-only: a changed value opens, never force-closes. Mirrors driver.jsx.
      openSignal={openSignalOnError}
    >
      <Form>
        <Grid spacing={4} container justifyContent="space-between" className={classes.cardBody}>
          <Field id={`${fieldName}.VIN`} name={`${fieldName}.VIN`} type="string" label="VIN" mode="light" xs={4} />
          <Field
            id={`${fieldName}.type`}
            name={`${fieldName}.type`}
            type="select"
            label="Type"
            mode="light"
            xs={4}
            options={trailerTypeOptions}
          />
          <Field
            id={`${fieldName}.primaryUse`}
            name={`${fieldName}.primaryUse`}
            type="select"
            label="Primary Use"
            mode="light"
            xs={4}
            options={lookupsJson.trailersPrimaryUse}
          />
          <Field
            id={`${fieldName}.purchaseDate`}
            name={`${fieldName}.purchaseDate`}
            type="date"
            label="Purchase Date"
            mode="light"
            xs={4}
          />
          <Field
            id={`${fieldName}.garageLocation.address`}
            name={`${fieldName}.garageLocation.address`}
            type="string"
            label="Garage Location"
            mode="light"
            xs={8}
          />
          <Field
            id={`${fieldName}.garageLocation.address2`}
            name={`${fieldName}.garageLocation.address2`}
            type="string"
            label="Address 2"
            mode="light"
            xs={4}
          />
          <Field
            id={`${fieldName}.garageLocation.city`}
            name={`${fieldName}.garageLocation.city`}
            type="string"
            label="City"
            mode="light"
            xs={4}
          />
          <Field
            id={`${fieldName}.garageLocation.state`}
            name={`${fieldName}.garageLocation.state`}
            type="state"
            label="State"
            mode="light"
            xs={4}
          />
          <Field
            id={`${fieldName}.garageLocation.zip`}
            name={`${fieldName}.garageLocation.zip`}
            type="string"
            label="Zip Code"
            mode="light"
            xs={4}
          />
          <Field
            id={`${fieldName}.year`}
            name={`${fieldName}.year`}
            type="string"
            label="Trailer year"
            mode="light"
            xs={4}
          />
          <Field
            id={`${fieldName}.value`}
            name={`${fieldName}.value`}
            type="string"
            label={statedAmountLabel ? 'Stated Amount' : 'Trailer value'}
            mode="light"
            xs={4}
            InputProps={{
              startAdornment: (
                <InputAdornment className={classes.inputAdornment} clposition="start">
                  $
                </InputAdornment>
              )
            }}
          />
          {!hideContents && (
            <Field
              id={`${fieldName}.contents`}
              name={`${fieldName}.contents`}
              type="string"
              label="Contents value"
              mode="light"
              xs={4}
              InputProps={{
                startAdornment: (
                  <InputAdornment className={classes.inputAdornment} clposition="start">
                    $
                  </InputAdornment>
                )
              }}
            />
          )}
        </Grid>
        <Grid spacing={4} container justifyContent="space-between" className={classes.containerDark} key={state}>
          <Grid item xs={6}>
            <LabelTooltip
              label="Comprehensive Deductible"
              tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.deductibleComprehensive }}
            >
              <Field
                id={`${fieldName}.deductibleComprehensive`}
                name={`${fieldName}.deductibleComprehensive`}
                type="select"
                mode="dark"
                // how do we change this over to lookupsJson?
                options={deductibleComprehensiveOptions}
              />
            </LabelTooltip>
          </Grid>

          <Grid item xs={6}>
            <LabelTooltip
              label="Collision Deductible"
              tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.deductibleCollision(state) }}
            >
              <Field
                id={`${fieldName}.deductibleCollision`}
                name={`${fieldName}.deductibleCollision`}
                type="select"
                mode="dark"
                // how do we change this over to lookupsJson?
                options={deductibleCollisionOptions}
              />
            </LabelTooltip>
          </Grid>
        </Grid>
      </Form>
    </CollapsibleCard>
  );
};

Trailer.propTypes = {
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.shape({
    VIN: PropTypes.string,
    addDate: PropTypes.string,
    confirmed: PropTypes.bool,
    deductibleCollision: PropTypes.string,
    deductibleComprehensive: PropTypes.string,
    coverageLoan: PropTypes.bool,
    garageLocation: PropTypes.object,
    primaryUse: PropTypes.string,
    purchaseDate: PropTypes.string,
    year: PropTypes.number
  }).isRequired,
  index: PropTypes.number.isRequired,
  removeFromList: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  offer: PropTypes.object,
  policy: PropTypes.object,
  // Bumped by form-list when this trailer is the validation-errors-modal scroll target, to open its card.
  openSignalOnError: PropTypes.number
};

Trailer.defaultProps = {
  disabled: false,
  offer: {},
  policy: {},
  openSignalOnError: 0
};

export default flowRight(withStyles(styles), withDatePicker)(memo(Trailer));
